import { useEffect, useMemo, useState } from "react";
import { Dish } from "./DishCard";

export interface SizeOption {
  id: string;
  label: string;
  price: number;
}

export interface AddonOption {
  id: string;
  label: string;
  price: number;
}

interface Props {
  open: boolean;
  dish: Dish | null;
  sizes?: SizeOption[];
  addons?: AddonOption[];
  image?: string;
  onClose: () => void;
  onAddToCart: (payload: {
    dish: Dish;
    sizeId: string | null;
    addonIds: string[];
    total: number;
  }) => void;
}

const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path d="M6 6l12 12M18 6L6 18" stroke="#111" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const CheckMark = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
    <path d="M5 12l5 5L20 7" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const Box = ({ checked, shape }: { checked: boolean; shape: "square" | "circle" }) => (
  <span className={`w-5 h-5 ${shape === "circle" ? "rounded-full" : "rounded-[4px]"} border-2 flex items-center justify-center shrink-0`}
    style={{ backgroundColor: checked ? "#F15A29" : "#FFFFFF", borderColor: checked ? "#F15A29" : "#C5C6CC", }}  >
    {checked && (shape === "circle" ? (<span className="w-2 h-2 rounded-full bg-white" />) : (<CheckMark />))}
  </span>
);

const DEFAULT_SIZES: SizeOption[] = [
  { id: "small", label: "Small", price: 0 },
  { id: "medium", label: "Medium", price: 100 },
  { id: "large", label: "Large", price: 200 },
];

const DEFAULT_ADDONS: AddonOption[] = [
  { id: "cheese", label: "Extra Cheese", price: 40 },
  { id: "toppings", label: "Extra Toppings", price: 40 },
];

const AddonsPopup = ({ open, dish, sizes = DEFAULT_SIZES, addons = DEFAULT_ADDONS, image, onClose, onAddToCart, }: Props) => {
  const [sizeId, setSizeId] = useState<string | null>(sizes[0]?.id ?? null);
  const [addonIds, setAddonIds] = useState<string[]>([]);

  useEffect(() => {
    if (open) {
      setSizeId(sizes[0]?.id ?? null);
      setAddonIds([]);
    }
  }, [open, sizes]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const total = useMemo(() => {
    if (!dish) return 0;
    const base = dish.price;
    const sizeBump = sizes.find((s) => s.id === sizeId)?.price ?? 0;
    const addonsTotal = addons
      .filter((a) => addonIds.includes(a.id))
      .reduce((acc, a) => acc + a.price, 0);
    return base + sizeBump + addonsTotal;
  }, [dish, sizes, sizeId, addons, addonIds]);

  if (!open || !dish) return null;

  const toggleAddon = (id: string) => {
    setAddonIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const currency = dish.currency ?? "₹";

  return (
    <div role="dialog" aria-modal="true"  // aria-label={`Customize ${dish.name}`}
      className="fixed inset-0 z-[60] flex items-end justify-center mt-5"
    >
      <div className="absolute inset-0 backdrop-blur-sm bg-black/70" onClick={onClose} aria-hidden="true" />

      <div className="relative w-full max-w-[430px] rounded-t-[20px] overflow-hidden max-h-[90vh] flex flex-col addons-sheet" style={{ backgroundColor: "#FFFFFF" }} >
        <style>
          {`@keyframes addonsSlideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
            .addons-sheet { animation: addonsSlideUp 240ms ease-out; }`}
        </style>
        <div className="flex justify-center pt-2 pb-1">
          <span className="block w-10 h-1 rounded-full bg-[#E8E9F1]" />
        </div>
        {/* Header */}
        <div className="flex items-start justify-between px-4 pt-4 pb-3">
          <h2 className="font-sansation font-bold text-[#111] text-[16px]">
            {dish.name}
          </h2>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="w-6 h-6 flex items-center justify-center bg-transparent border-0 p-0"
          >
            <CloseIcon />
          </button>
        </div>

        {/* Dish preview card */}
        <div className="mx-4 mb-4 bg-[#F8F8F8] border border-[#F4F4F5] rounded-[10px] flex items-center gap-3 p-3">
          {image ? (
            <img
              src={image}
              alt={dish.name}
              className="w-[60px] h-[50px] object-cover rounded-[8px]"
            />
          ) : (
            <div className="w-[60px] h-[50px] rounded-[8px] bg-white" />
          )}
          <div className="flex flex-col gap-0.5">
            <p className="font-opensans text-[14px] text-[#111]">#{dish.code}</p>
            <p className="font-sansation font-bold text-[#111] text-[16px]">
              {dish.name}
            </p>
          </div>
        </div>

        {/* Sizes */}
        <div className="px-4">
          <h3 className="font-sansation font-bold text-[#111] text-[14px] mb-3">
            Size
          </h3>
          <ul className="flex flex-col gap-3">
            {sizes.map((s) => {
              const checked = sizeId === s.id;
              return (
                <li key={s.id}>
                  <label className="flex items-center justify-between cursor-pointer">
                    <span className="flex items-center gap-2">
                      <Box checked={checked} shape="square" />
                      <span className="font-opensans text-[14px] text-[#111]">
                        {s.label}
                      </span>
                    </span>
                    <span className="font-sansation font-bold text-[#F15A29] text-[16px]">
                      {currency}
                      {dish.price + s.price}
                    </span>
                    <input
                      type="radio"
                      name="size"
                      checked={checked}
                      onChange={() => setSizeId(s.id)}
                      className="sr-only"
                    />
                  </label>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="h-px bg-[#E5E5E7] mx-4 my-4" />

        {/* Add Ons */}
        {addons.length > 0 && (
          <div className="px-4">
            <h3 className="font-sansation font-bold text-[#111] text-[14px] mb-3">
              Add On
            </h3>
            <ul className="flex flex-col">
              {addons.map((a, i) => {
                const checked = addonIds.includes(a.id);
                return (
                  <li key={a.id} className={`flex items-center justify-between py-3 ${i !== addons.length - 1 ? "border-b border-[#F0F0F1]" : ""}`} >
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Box checked={checked} shape="square" />
                      <span className="font-opensans text-[14px] text-[#111]">
                        {a.label}
                      </span>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleAddon(a.id)}
                        className="sr-only"
                      />
                    </label>
                    <span className="font-sansation font-bold text-[#F15A29] text-[16px]">
                      {currency}
                      {a.price}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* CTA */}
        <div className="px-4 pt-2 pb-4">
          <button
            type="button"
            onClick={() => onAddToCart({ dish, sizeId, addonIds, total })}
            className="w-full rounded-[6px] py-3.5 font-sansation font-bold text-[14px] tracking-[-0.28px] uppercase text-white border-0"
            style={{ backgroundColor: "#F15A29" }}
          >
            Add to cart · {currency}
            {total}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddonsPopup;
